'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { StaffTransaction, StaffPaymentInput } from '@garage/shared'
import { staffPaymentInputSchema } from '@garage/shared'
import { Dialog, TextField, Button, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters } from '@/lib/hooks.js'

interface StaffPaymentDialogProps {
  open: boolean
  workerId: string
  transaction?: StaffTransaction
  onClose: () => void
}

interface FormState {
  amountStr: string
  paidAt: string
  notes: string | null
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

export function StaffPaymentDialog({ open, workerId, transaction, onClose }: StaffPaymentDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const formatters = useFormatters()
  const { t } = useTranslation()

  const initialState = (): FormState => ({
    amountStr: '',
    paidAt: today(),
    notes: null,
  })

  const [formState, setFormState] = useState<FormState>(initialState)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormState(initialState())
      setFieldErrors({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transaction])

  const mutation = useMutation({
    mutationFn: async (input: StaffPaymentInput) =>
      api.workers.addPayment(workerId, transaction!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.worker(workerId) })
      queryClient.invalidateQueries({ queryKey: qk.workers() })
      snackbar.success(t('staffPaymentDialog.paymentRecorded'))
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handlePayAllRemaining = () => {
    if (transaction) {
      const remainingStr = String(transaction.remainingMinor / 100)
      setFormState((s) => ({ ...s, amountStr: remainingStr }))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!transaction) return

    const amountMinor = Math.round(parseFloat(formState.amountStr || '0') * 100)

    // Client-side validation for amount > remaining
    if (amountMinor > transaction.remainingMinor) {
      setFieldErrors({
        amountMinor: t('staffPaymentDialog.exceedsRemaining', {
          amount: formatters.money(transaction.remainingMinor),
        }),
      })
      return
    }

    const input: StaffPaymentInput = {
      amountMinor,
      paidAt: formState.paidAt,
      notes: formState.notes,
    }

    const result = staffPaymentInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        errors[issue.path.join('.')] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  if (!transaction) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('staffPaymentDialog.recordPayment')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {t('common.add')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <div className="text-label-md text-on-surface-variant font-medium">{t('workerDetail.whatFor')}</div>
          <div className="text-body-md text-on-surface">{transaction.reason}</div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-label-md text-on-surface-variant font-medium">{t('workerDetail.amount')}</div>
            <div className="text-body-md text-on-surface">{formatters.money(transaction.amountMinor)}</div>
          </div>
          <div>
            <div className="text-label-md text-on-surface-variant font-medium">{t('staffPaymentDialog.amount')}</div>
            <div className="text-body-md text-on-surface">{formatters.money(transaction.paidMinor)}</div>
          </div>
        </div>

        <div className="bg-surface-low rounded p-3">
          <div className="text-label-md text-on-surface-variant">{t('staffPaymentDialog.paidProgress', {
            paid: formatters.money(transaction.paidMinor),
            remaining: formatters.money(transaction.remainingMinor),
          })}</div>
        </div>

        <div className="space-y-1">
          <TextField
            label={t('staffPaymentDialog.amount')}
            value={formState.amountStr}
            onChange={(v) => setFormState((s) => ({ ...s, amountStr: v }))}
            error={fieldErrors.amountMinor}
            numeric
            fullWidth
          />
          <button
            type="button"
            onClick={handlePayAllRemaining}
            className="text-label-sm text-primary hover:text-primary-variant"
          >
            {t('staffPaymentDialog.payAllRemaining')}
          </button>
        </div>

        <TextField
          label={t('staffPaymentDialog.date')}
          type="date"
          value={formState.paidAt}
          onChange={(v) => setFormState((s) => ({ ...s, paidAt: v }))}
          error={fieldErrors.paidAt}
          fullWidth
        />

        <TextField
          label={t('staffPaymentDialog.note')}
          value={formState.notes ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, notes: v || null }))}
          error={fieldErrors.notes}
          multiline
          rows={2}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
